import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { adminFetchStats } from '../store/slices/adminSlice';
import { pushToast } from '../store/slices/toastSlice';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { user, token, bootstrapped } = useSelector((state) => state.auth);
  const { metrics, recentUsers, recentAudit, isLoading } = useSelector(s => s.admin);

  useEffect(() => {
    if (user?.accountType === 'ADMIN') {
      dispatch(adminFetchStats())
        .unwrap()
        .catch(err => dispatch(pushToast({ type:'error', message: err || 'Failed to load stats'})));
    }
  }, [dispatch, user]);

  const getRoleSpecificContent = () => {
    switch (user?.accountType) {
      case 'WHS':
        return (
          <div className="dashboard-grid">
            <QuickCard title="Products" desc="Manage catalog & inventory" to="/products" />
            <QuickCard title="RFQs & Orders" desc="Respond & fulfill" to="/rfqs" />
            <QuickCard title="Documents" desc="Compliance files" to="/documents" />
            <QuickCard title="Analytics" desc="Performance metrics" to="/analytics" />
          </div>
        );
      case 'RTL':
        return (
          <div className="dashboard-grid">
            <QuickCard title="Browse" desc="Discover products" to="/browse" />
            <QuickCard title="RFQs & Orders" desc="Track progress" to="/rfqs" />
            <QuickCard title="Saved" desc="Favorites & wishlist" to="/saved" />
            <QuickCard title="Documents" desc="Account documents" to="/documents" />
          </div>
        );
      case 'ADMIN':
        return (
          <div className="admin-dashboard">
            <h2 style={{ marginTop:'1rem' }}>Platform Metrics</h2>
            {!metrics && isLoading && <p>Loading metrics...</p>}
            {!metrics && !isLoading && <p style={{ color:'#666' }}>No metrics available.</p>}
            {metrics && (
              <div className="metric-grid" style={{ display:'grid', gap:'1rem', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', marginBottom:'1.5rem' }}>
                <MetricCard label="Total Users" value={metrics.totalUsers} />
                <MetricCard label="Active Wholesalers" value={metrics.activeWholesalers} />
                <MetricCard label="Active Retailers" value={metrics.activeRetailers} />
                <MetricCard label="Pending Verification" value={metrics.pendingVerification} />
                <MetricCard label="Products" value={metrics.totalProducts} />
                <MetricCard label="Active Products" value={metrics.activeProducts} />
                <MetricCard label="Hidden Products" value={metrics.hiddenProducts} />
              </div>
            )}
            <div className="admin-flex" style={{ display:'grid', gap:'1.5rem', gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))' }}>
              <div className="dashboard-card" style={{ padding:'1rem', border:'1px solid #e0e0e0', borderRadius:6, background:'#fff' }}>
                <h3 style={{ marginTop:0 }}>Recent Signups</h3>
                {!recentUsers?.length && <p style={{ fontSize:'0.85rem' }}>No recent users.</p>}
                {recentUsers?.length > 0 && (
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8rem' }}>
                    <thead><tr><th style={thMini}>Company</th><th style={thMini}>Role</th><th style={thMini}>Status</th><th style={thMini}>Joined</th></tr></thead>
                    <tbody>
                      {recentUsers.map(u => (
                        <tr key={u._id} style={{ borderBottom:'1px solid #eee' }}>
                          <td style={tdMini}>{u.companyName}</td>
                          <td style={tdMini}>{u.accountType}</td>
                          <td style={tdMini}>{u.verification?.status}</td>
                          <td style={tdMini}>{new Date(u.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="dashboard-card" style={{ padding:'1rem', border:'1px solid #e0e0e0', borderRadius:6, background:'#fff' }}>
                <h3 style={{ marginTop:0 }}>Recent Activity</h3>
                {!recentAudit?.length && <p style={{ fontSize:'0.85rem' }}>No recent audit logs.</p>}
                {recentAudit?.length > 0 && (
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8rem' }}>
                    <thead><tr><th style={thMini}>When</th><th style={thMini}>Actor</th><th style={thMini}>Action</th><th style={thMini}>Target</th></tr></thead>
                    <tbody>
                      {recentAudit.map(a => (
                        <tr key={a._id} style={{ borderBottom:'1px solid #eee' }}>
                          <td style={tdMini}>{new Date(a.createdAt).toLocaleTimeString()}</td>
                          <td style={tdMini}>{a.actor ? a.actor.companyName : 'System'}</td>
                          <td style={tdMini}>{a.action}</td>
                          <td style={tdMini}>{a.targetType}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        );
      default:
        if (token && !bootstrapped) return <p>Authenticating...</p>;
        return <p>Loading dashboard...</p>;
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Welcome{user ? `, ${user.companyName}` : ''}</h1>
        <p className="dashboard-subtitle">
          {user?.accountType === 'WHS' && 'Manage your wholesale business'}
          {user?.accountType === 'RTL' && 'Discover wholesale opportunities'}
          {user?.accountType === 'ADMIN' && 'Platform administration dashboard'}
        </p>
      </div>

      {user?.accountType === 'WHS' && user?.verification?.status !== 'APPROVED' && (
        <div className="alert alert-warning">
          <strong>Verification Required:</strong> Your account is pending verification. 
          Upload your business documents to start listing products.
        </div>
      )}

      {getRoleSpecificContent()}
    </div>
  );
};

export default Dashboard;

const thMini = { textAlign:'left', padding:'4px', borderBottom:'1px solid #ddd' };
const tdMini = { padding:'4px' };

const MetricCard = ({ label, value }) => (
  <div style={{ background:'#fff', border:'1px solid #e0e0e0', borderRadius:6, padding:'0.85rem', display:'flex', flexDirection:'column', gap:'0.35rem' }}>
    <span style={{ fontSize:'0.75rem', fontWeight:600, letterSpacing:0.5, color:'#555', textTransform:'uppercase' }}>{label}</span>
    <span style={{ fontSize:'1.4rem', fontWeight:600 }}>{value}</span>
  </div>
);

const QuickCard = ({ title, desc, to }) => (
  <Link to={to} className="dashboard-card" style={{ textDecoration:'none', color:'inherit' }}>
    <h3>{title}</h3>
    <p>{desc}</p>
  </Link>
);